// js/common/animation.js
import { gsap, ScrollTrigger } from "@/js/common/gsap.js";

/**
 * =========================================================
 * Animation
 * - FadeIn: 通常フェードイン
 * - DonIn: 赤文字などの強調出現
 * =========================================================
 */

/* =========================
   Utilities
========================= */

export function guardOnce(el, key) {
  const attr = `data-anim-${key}`;

  if (el.hasAttribute(attr)) return false;

  el.setAttribute(attr, "1");
  return true;
}

function $$(scope, selector) {
  return Array.from((scope || document).querySelectorAll(selector));
}

function prefersReduce() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function refreshST() {
  if (ScrollTrigger?.refresh) {
    ScrollTrigger.refresh();
    return;
  }

  if (window.ScrollTrigger?.refresh) {
    window.ScrollTrigger.refresh();
  }
}

/* =========================
   FadeIn
========================= */

export function initFadeIn({ scope = document } = {}) {
  const items = $$(scope, ".js-fade-in").filter((el) =>
    guardOnce(el, "fadeIn"),
  );

  if (!items.length) return;

  if (prefersReduce()) {
    gsap.set(items, {
      opacity: 1,
    });
    return;
  }

  gsap.set(items, {
    opacity: 0,
  });

  items.forEach((item) => {
    const snapPanel = item.closest(".js-snap-panel");

    // js-snap-panel内はplayFadeIn()で再生する
    if (snapPanel) return;

    gsap.fromTo(
      item,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        duration: 1,
        ease: "power1.out",
        scrollTrigger: {
          trigger: item,
          start: "top 70%",
          toggleActions: "play reverse play reverse",
        },
      },
    );
  });
}

/* =========================
   DonIn 初期状態
   - 最終サイズより大きい状態で非表示
   - 小さい状態から拡大させない
========================= */

function setDonInInitial(items) {
  gsap.set(items, {
    opacity: 0,
    scaleX: 1.55,
    scaleY: 1.55,
    transformOrigin: "center center",
    force3D: true,
  });
}

/* =========================
   DonIn タイムライン
   - 印影が強く押された瞬間を表現
   - 位置は移動させない
   - 接触 → 反動 → 収束
========================= */

function createDonInTimeline(
  items,
  {
    delay = 0,
    stagger = 0,
    scrollTrigger = null,
  } = {},
) {
  const timelineOptions = {
    delay,
  };

  if (scrollTrigger) {
    timelineOptions.scrollTrigger = scrollTrigger;
  }

  return gsap
    .timeline(timelineOptions)

    // スタンプ本体を表示
    .set(items, {
      opacity: 1,
    })

    // 強く押し込む
    .to(items, {
      scaleX: 1.16,
      scaleY: 0.76,
      duration: 0.14,
      ease: "expo.in",
      stagger,
    })

    // 接地した状態を一瞬維持する
    .to(items, {
      scaleX: 1.15,
      scaleY: 0.75,
      duration: 0.045,
      ease: "none",
      stagger,
    })

    // 押し返される反動
    .to(items, {
      scaleX: 0.95,
      scaleY: 1.1,
      duration: 0.12,
      ease: "power4.out",
      stagger,
    })

    // 小さく再接地する
    .to(items, {
      scaleX: 1.05,
      scaleY: 0.95,
      duration: 0.085,
      ease: "power2.inOut",
      stagger,
    })

    // 通常サイズへ収束
    .to(items, {
      scaleX: 1,
      scaleY: 1,
      duration: 0.15,
      ease: "power3.out",
      stagger,
      clearProps: "transform,transformOrigin",
    });
}

/* =========================
   DonIn 最終状態
========================= */

function setDonInComplete(items) {
  gsap.set(items, {
    opacity: 1,
    scaleX: 1,
    scaleY: 1,
    transformOrigin: "center center",
  });
}


/* =========================
   DonIn 通常スクロール用
========================= */

export function initDonIn({ scope = document } = {}) {
  const items = $$(scope, ".js-don-in").filter((el) =>
    guardOnce(el, "donIn"),
  );

  if (!items.length) return;

  if (prefersReduce()) {
    setDonInComplete(items);
    return;
  }

  setDonInInitial(items);

  items.forEach((item) => {
    const snapPanel = item.closest(".js-snap-panel");

    // js-snap-panel内はplayDonIn()で再生する
    if (snapPanel) return;

    createDonInTimeline(item, {
      scrollTrigger: {
        trigger: item,
        start: "top 80%",
        once: true,
      },
    });
  });
}

/* =========================
   js-snap-panel用 FadeIn
========================= */

export function playFadeIn(scope) {
  const items = $$(scope, ".js-fade-in");

  if (!items.length) return;

  if (prefersReduce()) {
    gsap.set(items, {
      opacity: 1,
    });
    return;
  }

  gsap.to(items, {
    opacity: 1,
    duration: 1,
    delay: 0.75,
    ease: "power1.out",
  });
}

/* =========================
   js-snap-panel用 DonIn
========================= */

export function playDonIn(scope) {
  const items = $$(scope, ".js-don-in").filter((el) =>
    guardOnce(el, "donInPlayed"),
  );

  if (!items.length) return;

  if (prefersReduce()) {
    setDonInComplete(items);
    return;
  }

  setDonInInitial(items);

  createDonInTimeline(items, {
    delay: 0.75,
    stagger: 0.06,
  });
}

/* =========================
   js-snap-panel用 一括再生関数
========================= */

export function playPanelAnimations(scope) {
  playFadeIn(scope);
  playDonIn(scope);
}

/* =========================
   初期化関数
========================= */

export function initAnimations({ scope = document } = {}) {
  initFadeIn({ scope });
  initDonIn({ scope });

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      refreshST();
    });
  });
}

/* =========================
   手動 refresh 用フック
========================= */

export function refreshScrollTrigger() {
  refreshST();
}